// Builds the Ship Happens study guide as a PDF, in the browser.
//
// The whole field guide — every ship, its teaching, its Ship Check — with the
// reader's own answers set into it where they have written one and ruled
// lines where they have not. It is a workbook they can keep, not an export of
// a form. Nothing leaves the device: the answers come out of localStorage and
// go straight into the file.
//
// The page furniture lives in brandPdf.js, shared with every other document
// the app renders. Only what is particular to this series is here.
import {
  INK,
  PAPER,
  DIM,
  PAGE,
  MARGIN,
  WIDTH,
  THEMES,
  Writer,
  imageData,
  newDoc,
} from "./brandPdf.js";

const ACCENT = THEMES.ship.accent;
const ACCENT_HI = THEMES.ship.accentInk;

/** The running footer on every page but the cover. */
function footers(doc, series) {
  const n = doc.getNumberOfPages();
  for (let p = 2; p <= n; p++) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...DIM);
    doc.text(`${series.title} · ${series.source.author}`, MARGIN, PAGE.h - 28);
    doc.text(`${p - 1}`, PAGE.w - MARGIN, PAGE.h - 28, { align: "right" });
  }
}

/**
 * @param series  the series JSON
 * @param answers { [shipId]: string[] } — one entry per Ship Check prompt
 */
export async function buildGuide(series, answers) {
  const doc = await newDoc();
  const w = new Writer(doc);

  // ---- cover: ink, the lockup, the book ----
  doc.setFillColor(...INK);
  doc.rect(0, 0, PAGE.w, PAGE.h, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...ACCENT_HI);
  doc.text(`FROM THE BOOK BY ${series.source.author}`.toUpperCase(), MARGIN, 120, { charSpace: 1.6 });

  const title = series.title.replace(/\.$/, "");
  doc.setFontSize(64);
  doc.setTextColor(...PAPER);
  const titleLines = doc.splitTextToSize(title, WIDTH);
  doc.text(titleLines, MARGIN, 190);
  // The period in the accent, the way the cover sets it.
  const last = titleLines[titleLines.length - 1];
  const lastW = doc.getTextWidth(last);
  doc.setTextColor(...ACCENT);
  doc.text(".", MARGIN + lastW, 190 + (titleLines.length - 1) * 66);

  const afterTitle = 190 + (titleLines.length - 1) * 66 + 34;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(14);
  doc.setTextColor(...PAPER);
  doc.text(doc.splitTextToSize(series.subtitle, 340), MARGIN, afterTitle);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...DIM);
  doc.text("FIELD GUIDE & SHIP CHECK JOURNAL", MARGIN, afterTitle + 60, { charSpace: 1.8 });

  const cover = await imageData(series.assets.coverSmall);
  if (cover) {
    // 2:3-ish book proportions, tucked bottom right.
    const cw = 180;
    const ch = Math.round(cw * (2560 / 1812));
    doc.addImage(cover, "JPEG", PAGE.w - MARGIN - cw, PAGE.h - MARGIN - ch, cw, ch);
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...DIM);
  const stamp = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  doc.text(`Saved ${stamp} · ${series.location}`, MARGIN, PAGE.h - MARGIN);

  // ---- premise + how to use ----
  w.page();
  w.label("Before you abandon ship", ACCENT);
  w.gap(4);
  for (const p of series.premise) w.body(p, { size: 12, after: 10 });
  w.gap(10);
  w.rule();
  w.label("How to use this");
  w.body(
    "Ten ships, one at a time. Each has its teaching, then a Ship Check — the questions worth sitting with. Where you had already written an answer in the app it is printed here; where you had not, there is room to write by hand. Nothing in this file was sent anywhere. It came straight from your device to this page.",
    { size: 10.5 },
  );

  // ---- the fleet ----
  for (const ship of series.fleet) {
    w.page();
    const n = String(ship.order).padStart(2, "0");

    // Hull number, oversized and grey, behind the name.
    doc.setFont("helvetica", "bold");
    doc.setFontSize(96);
    doc.setTextColor(232, 232, 229);
    doc.text(n, PAGE.w - MARGIN, MARGIN + 74, { align: "right" });

    w.label(ship.vessel, ACCENT);
    w.heading(ship.name, 30);
    w.body(ship.question, { italic: true, size: 12.5, after: 14 });
    w.rule(INK);
    w.gap(6);

    w.field("Built for", ship.builtFor);
    w.field("The lesson", ship.lesson);
    w.field("The value", ship.value);
    w.field("The danger", ship.danger);
    w.quote(ship.pullQuote);
    w.field("Anchor", ship.scripture.map((s) => s.ref).join("  ·  "));

    // Every ship is a spread: the teaching on one page, the journal on the
    // next. Squeezing both onto one page left seven of ten ships spilling a
    // single prompt onto an otherwise empty sheet; two pages by design gives
    // every ship the same shape and every question room to be answered by
    // hand.
    w.page();
    w.label(`${n} · ${ship.name}`, DIM);
    w.heading("Ship check", 30);
    w.body(ship.question, { italic: true, size: 12.5, after: 14 });
    w.rule(ACCENT);
    w.gap(10);
    const mine = answers[ship.id] || [];
    ship.shipCheck.forEach((q, i) => w.prompt(q, mine[i]));
  }

  // ---- closing + the book ----
  w.page();
  w.label("Ship happens", ACCENT);
  w.gap(4);
  for (const p of series.closing) w.body(p, { size: 12, after: 10 });
  w.gap(18);
  w.rule();
  w.label("The book");
  w.body(`${series.source.title} — ${series.source.author}, ${series.source.year}`, {
    bold: true,
    size: 12,
    after: 4,
  });
  w.body(series.book.blurb, { size: 10.5, after: 8 });
  if (series.book.purchaseUrl) {
    w.ensure(16);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.setTextColor(...ACCENT);
    doc.textWithLink(series.book.purchaseLabel || "Get the book", MARGIN, w.y + 9, {
      url: series.book.purchaseUrl,
    });
    w.y += 20;
  }
  if (series.book.inPerson) w.body(series.book.inPerson, { size: 10, color: DIM });

  footers(doc, series);
  return doc;
}

export { deliver } from "./brandPdf.js";
